import React from 'react';
import { ButtonCommon } from 'components/common';
import { Info, CalendarBlank, ArrowRight, Tag, House, Kanban } from '@phosphor-icons/react';
import { tv, type VariantProps } from 'tailwind-variants';
import { formatDate, formatPrice } from 'utils';
import { DateDisplayProps } from 'types';
import { statusOutLabels, StockOutOrderStatusCode, StockOutOrderType } from 'models/InventoryDocument';

// Define all card variants using tv instead of cva
export const cardStyles = tv({
  base: 'bg-white rounded-lg overflow-hidden',
  variants: {
    status: {
      DRAFT: '',
      WAIT_CONFIRM: '',
      CONFIRMED: '',
      EXPORTED: '',
      CANCELLED: '',
    },
    isCompact: {
      true: '',
      false: '',
    },
  },
  defaultVariants: {
    status: 'DRAFT',
    isCompact: false,
  },
});

const statusBar = tv({
  base: 'rounded',
  variants: {
    status: {
      CONFIRMED: 'bg-[var(--sematicErrorColor600)]',
      EXPORTED: 'bg-[var(--tealColor700)]',
      WAIT_CONFIRM: 'bg-[var(--primaryColor500)]',
      CANCELLED: 'bg-[var(--sematicErrorColor500)]',
      DRAFT: 'bg-[var(--textBlack56)]',
    },
    isCompact: {
      true: 'w-2 h-[64px] self-stretch',
      false: 'w-2 h-[64px]',
    },
  },
  defaultVariants: {
    status: 'DRAFT',
    isCompact: false,
  },
});

const statusDot = tv({
  base: 'w-1.5 h-1.5 rounded-full',
  variants: {
    status: {
      CONFIRMED: 'bg-[var(--sematicErrorColor600)]',
      EXPORTED: 'bg-[var(--tealColor700)]',
      WAIT_CONFIRM: 'bg-[var(--primaryColor500)]',
      CANCELLED: 'bg-[var(--sematicErrorColor500)]',
      DRAFT: 'bg-[var(--textBlack56)]',
    },
  },
  defaultVariants: {
    status: 'DRAFT',
  },
});

const statusText = tv({
  base: 'text-sm font-bold',
  variants: {
    status: {
      CONFIRMED: 'text-[var(--sematicErrorColor600)]',
      EXPORTED: 'text-[var(--tealColor700)]',
      WAIT_CONFIRM: 'text-[var(--primaryColor500)]',
      CANCELLED: 'text-[var(--sematicErrorColor500)]',
      DRAFT: 'text-[var(--textBlack56)]',
    },
  },
  defaultVariants: {
    status: 'DRAFT',
  },
});

// Date display styling
const dateDisplay = tv({
  base: 'h-8 px-4 py-2.5 rounded-full flex items-center gap-2',
  variants: {
    variant: {
      default: 'bg-[var(--bgBlackOpacity)]',
      error: 'bg-[var(--bgRed50)]',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

// Date icon styling
const dateIcon = tv({
  base: 'text-xl',
  variants: {
    variant: {
      default: 'text-[var(--textBlack100)]',
      error: 'text-[var(--sematicErrorColor500)]',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

// Date text styling
const dateText = tv({
  base: 'text-center text-sm leading-5',
  variants: {
    variant: {
      default: 'text-[var(--textBlack100)]',
      error: 'text-[var(--sematicErrorColor500)]',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

// Date display component with calendar icon

const DateDisplay: React.FC<DateDisplayProps> = ({ label, date, variant = 'default' }) => {
  return (
    <div className='h-8 flex items-center gap-2'>
      <div className='text-[14px] leading-5 text-[rgba(0,0,0,0.56)]'>{label}</div>
      <div className={dateDisplay({ variant })}>
        <CalendarBlank size={20} className={dateIcon({ variant })} />
        <div className={dateText({ variant })}>{formatDate(date)}</div>
      </div>
    </div>
  );
};
const getIconForType = (type: StockOutOrderType) => {
  switch (type) {
    case StockOutOrderType.SALE:
      return (
        <span className='flex gap-2 items-center text-[var(--primaryColor500)]'>
          <Tag size={16} />
          Xuất bán hàng
        </span>
      );
    case StockOutOrderType.TRANSFER:
      return (
        <span className='flex gap-2 items-center text-[var(--tealColor700)]'>
          <House size={16} /> Xuất chuyển kho
        </span>
      );
    case StockOutOrderType.PRODUCTION:
      return (
        <span className='flex gap-2 items-center text-[var(--sematicSuccessColor500)]'>
          <Kanban size={16} />
          Xuất sản xuất
        </span>
      );
    case StockOutOrderType.RETURN:
      return (
        <span className='flex gap-2 items-center text-[var(--sematicErrorColor600)]'>
          <House size={16} />
          Xuất Trả hàng
        </span>
      );
    case StockOutOrderType.WASTE:
      return (
        <span className='flex gap-2 items-center text-[var(--sematicErrorColor500)]'>
          <Kanban size={16} /> Hàng hỏng
        </span>
      );
    case StockOutOrderType.SAMPLE:
      return (
        <span className='flex gap-2 items-center text-[var(--textBlack56)] '>
          <Kanban size={16} /> Mẫu thử
        </span>
      );
    default:
      return (
        <span className='flex gap-2 items-center text-[var(--textBlack100)]'>
          <Kanban size={16} /> Khác
        </span>
      );
  }
};
export interface StockOutOrderCardProps {
  code: string;
  createdAt: Date | string;
  deliveryDate: Date | string;
  warehouseName: string;
  status: StockOutOrderStatusCode;
  isCompact?: boolean;
  total: number;
  type?: StockOutOrderType;
  productCount?: number;
  onDetail?: () => void;
  onExport?: () => void;
  onView?: () => void;
}
export const StockOutOrderCard: React.FC<StockOutOrderCardProps> = ({
  code,
  createdAt,
  deliveryDate,
  warehouseName,
  status,
  total = 0,
  type,
  productCount = 0,
  isCompact = false,
  onDetail,
  onExport,
  // onView,
}) => {
  const mappedStatus =
    status === StockOutOrderStatusCode.CANCELLED
      ? 'CANCELLED'
      : status === StockOutOrderStatusCode.WAIT_CONFIRM
        ? 'WAIT_CONFIRM'
        : status === StockOutOrderStatusCode.CONFIRMED
          ? 'CONFIRMED'
          : status === StockOutOrderStatusCode.EXPORTED
            ? 'EXPORTED'
            : 'DRAFT';
  return (
    <div className={cardStyles({ status: mappedStatus, isCompact })}>
      <div className='px-6 py-3 flex flex-col gap-3'>
        <div className='flex justify-between items-start border-b py-3'>
          <div className='flex items-center gap-4'>
            <div className={statusBar({ status: mappedStatus, isCompact: true })}></div>

            <div className='flex flex-col gap-2'>
              <div className='flex items-center gap-2'>
                <span className='text-base font-bold text-[var(--textBlack100)]'>{code}</span>
                <div className='flex items-center gap-2'>
                  <div
                    className='w-3 h-3 p-[3px] rounded-full flex items-center justify-center'
                    style={{
                      background: `radial-gradient(ellipse 50% 50% at 50% 50%, ${
                        mappedStatus === 'CANCELLED'
                          ? 'var(--sematicErrorColor500)'
                          : mappedStatus === 'WAIT_CONFIRM'
                            ? 'var(--PrimaryColor500)'
                            : mappedStatus === 'CONFIRMED'
                              ? 'var(--sematicErrorColor600)'
                              : mappedStatus === 'EXPORTED'
                                ? 'var(--tealColor700)'
                                : 'var(--textBlack56)'
                      } 0%, rgba(59, 130, 246, 0) 100%)`,
                    }}
                  >
                    <div className={statusDot({ status: mappedStatus })}></div>
                  </div>
                  <span className={statusText({ status: mappedStatus })}>{statusOutLabels[status]}</span>
                </div>
              </div>

              {/* Warehouse name */}
              <div className='flex items-center gap-2'>
                <div className='h-8 px-4 py-1.5 bg-[var(--bgBlackOpacity)] rounded-full w-max text-sm text-[var(--textBlack100)]'>
                  {getIconForType(type || StockOutOrderType.OTHER)}
                </div>
                <div className='h-8 px-4 py-1.5 bg-[var(--bgBlackOpacity)] rounded-full w-max'>
                  <span className='text-sm text-[var(--textBlack100)]'>{warehouseName}</span>
                </div>
                <div className='h-8 px-4 py-1.5 bg-[var(--bgBlackOpacity)] rounded-full w-max'>
                  <span className='text-sm text-[var(--textBlack100)]'>{productCount} sản phẩm</span>
                </div>
              </div>
            </div>
          </div>

          {/* Chi tiết button */}
          <div className='flex items-center'>
            <ButtonCommon
              color='none'
              size='md'
              radius='rounded'
              className='hover:font-normal'
              icon={<Info size={16} />}
              iconPosition='right'
              onClick={onDetail}
            >
              Chi tiết
            </ButtonCommon>
          </div>
        </div>

        {/* Bottom section with dates and action buttons */}
        <div className='flex justify-between items-center'>
          <div className='flex flex-wrap items-center gap-x-4 gap-y-4'>
            <span className='text-sm text-[var(--textBlack56)]'>
              Giá trị phiếu <span className='font-bold text-[var(--primaryColor500)]'>{formatPrice(total)} VND</span>
            </span>
            <DateDisplay label='Ngày tạo phiếu' date={createdAt ? new Date(createdAt) : new Date()} />
            <DateDisplay label='Ngày xuất kho' date={deliveryDate} />
          </div>

          {/* Action buttons */}
          <div>
            {status === StockOutOrderStatusCode.CONFIRMED ? (
              <ButtonCommon color='primary' size='md' radius='rounded' className='w-[220px]' onClick={onExport}>
                Bắt đầu xuất kho
              </ButtonCommon>
            ) : (
              <ButtonCommon
                color='outline'
                size='md'
                radius='rounded'
                className='w-[220px]'
                icon={<ArrowRight size={16} />}
                iconPosition='right'
                onClick={onDetail}
              >
                Xem ngay
              </ButtonCommon>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
export type CardStylesVariantProps = VariantProps<typeof cardStyles>;
export default StockOutOrderCard;
